import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import clsx from 'clsx';
import _range from 'lodash/range';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import EmptySources from '@/assets/icons/empty-sources.svg';
import InformationIcon from '@/assets/icons/information.svg';
import { Counter } from '@/components/ui/counter';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from '@/components/ui/tag';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';

function UnmappedUtmSourcesContent({
  setUnmappedUtmCount,
}: {
  setUnmappedUtmCount: (count: number) => void;
}) {
  const intl = useIntl();

  const {
    data: { utm_source_mapping_keys: utmMappings },
  } = useSuspenseQuery({
    queryKey: [...queryKeys.settings.channels.utmMapping],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingSettingsUtmSourceMappingKeys(),
  });

  const unmappedUtmSources = useMemo(
    () => utmMappings.filter(utmMapping => !utmMapping.channel_id),
    [utmMappings],
  );

  useEffect(() => {
    setUnmappedUtmCount(unmappedUtmSources.length);
  }, [setUnmappedUtmCount, unmappedUtmSources]);

  if (!unmappedUtmSources || unmappedUtmSources.length === 0)
    return (
      <EmptyState
        className="w-full"
        icon={EmptySources}
        header={intl.formatMessage({
          defaultMessage: 'No sources detected yet',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'Applicants from external job boards or custom apply links will appear here once traffic starts coming in.',
        })}
      />
    );

  return (
    <div className="flex flex-wrap gap-2">
      {unmappedUtmSources.map(utmMapping => (
        <Tag
          key={utmMapping.mapping_key_id}
          text={intl.formatMessage(
            {
              defaultMessage: '{value} • {applicant_count} applicants',
              description: 'UTM source value and applicant count',
            },
            {
              value: utmMapping.value,
              applicant_count: utmMapping.applicant_count,
            },
          )}
          className="bg-fx-neutral-100"
        />
      ))}
    </div>
  );
}

UnmappedUtmSourcesContent.Skeleton =
  function UnmappedUtmSourcesContentSkeleton() {
    return (
      <div className="flex flex-wrap gap-2">
        {_range(9).map(index => (
          <Skeleton
            key={index}
            className={clsx(
              'h-6 !rounded-full',
              index % 3 === 0 ? 'w-50' : 'w-32',
            )}
          />
        ))}
      </div>
    );
  };

export function UnmappedUtmSources() {
  const [unmappedUtmCount, setUnmappedUtmCount] = useState(0);

  return (
    <div className="flex flex-col gap-4 px-6 py-4">
      {/* Title and description */}
      <div className="flex items-center gap-2">
        <Typography variant="medium2" as="h2">
          <FormattedMessage
            defaultMessage="Unmapped UTM sources"
            description="Title for the unmapped UTM sources"
          />
        </Typography>
        {unmappedUtmCount > 0 && <Counter count={unmappedUtmCount} />}
      </div>
      <Typography variant="small1">
        <FormattedMessage
          defaultMessage="These sources have been detected from applicant traffic but aren’t assigned to a channel yet. Assign them to a channel to include them in reporting."
          description="Description for the unmapped UTM sources"
        />
      </Typography>

      {/* Unmapped UTM sources */}
      <Suspense fallback={<UnmappedUtmSourcesContent.Skeleton />}>
        <UnmappedUtmSourcesContent setUnmappedUtmCount={setUnmappedUtmCount} />
      </Suspense>

      {/* Information icon and description */}
      <div className="flex gap-2">
        <img src={InformationIcon} alt="Information" />
        <Typography variant="small1" className="!text-fx-neutral-600">
          <FormattedMessage
            defaultMessage="Don’t see your UTM source? It may not have received any applicants yet."
            description="Description for the unmapped UTM sources"
          />
        </Typography>
      </div>
    </div>
  );
}
